import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { FakeLink } from '@/core/FakeLink';
import { faChevronDown, faChevronUp, faInfoCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';

export interface LoginInfoMessageProps {
  content: string;
}

/**
 * Renders the customer-configurable login info message (Features/Login/InfoMessage). Truncates content that
 * overflows two lines behind a "Read more"/"Read less" toggle so long admin-configured messages don't dominate
 * the login card.
 */
export const LoginInfoMessage = ({ content }: LoginInfoMessageProps) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (el) {
      setOverflows(el.scrollHeight > el.clientHeight);
    }
  }, [content]);

  return (
    <div className="tw:mb-4 tw:flex tw:items-start tw:gap-2 tw:rounded-sm tw:border tw:border-sq-bg-info-border tw:bg-sq-bg-info tw:p-3">
      <Icon icon={faInfoCircle} extraClassNames="tw:mt-0.5 tw:text-sq-bg-info-border" />
      <div>
        <div ref={contentRef} className={expanded ? undefined : 'tw:line-clamp-2'}>
          <ContainerWithHTML id="loginMessage" content={content} isBlock={true} />
        </div>
        {overflows && (
          <FakeLink
            testId="loginMessageReadMoreLink"
            onClick={() => setExpanded(!expanded)}
            extraClassNames="tw:underline">
            {t(expanded ? 'LOGIN_PANEL.READ_LESS' : 'LOGIN_PANEL.READ_MORE')}
            <Icon icon={expanded ? faChevronUp : faChevronDown} extraClassNames="ml5" />
          </FakeLink>
        )}
      </div>
    </div>
  );
};
